import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { InterfaceSelectList } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { AppHelper } from '@app/core/app.helper';
import { CadeSysPluginData } from '@components/fields/file/interfaces/file.component.interface';
import { Certificate } from 'crypto-pro-ts/dist/certificate';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-change-crypto-pro-certificate-dialog',
    templateUrl: './change-crypto-pro-certificate-dialog.component.html',
    styleUrls: ['./change-crypto-pro-certificate-dialog.component.css'],
})
export class ChangeCryptoProCertificateDialogComponent implements OnInit, OnDestroy {
    /** Ссылка на селект */
    @ViewChild('matSelect', { static: false }) matSelect;
    /** Ссылка на объект формы */
    @ViewChild('formRef', { static: true }) formRef: ElementRef;

    /** Объект формы */
    public form: FormGroup = null;

    private _onDestroy = new Subject<void>();

    /** Алгоритм сертификата */
    certificateAlgorithm: any = null;

    /** Статус сертификата */
    certificateStatus: string = null;

    public readonly listItems: InterfaceSelectList[] = [];

    /**
     * Конструктор
     * @param formBuilder создатель форм
     * @param appService сервис взаимодействия с внешними ресурсами
     * @param dialogRef ссылка на диалог
     * @param deviceService сервис определения устройства
     * @param data данные для диалога
     */
    constructor(
        private formBuilder: FormBuilder,
        public appService: AppService,
        public dialogRef: MatDialogRef<ChangeCryptoProCertificateDialogComponent>,
        public deviceService: DeviceDetectorService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            certificates: Certificate[];
            systemInfo: CadeSysPluginData;
        },
    ) {
        this.initList(data.certificates);
    }

    /** Инициализация списка */
    private initList(list: Certificate[]) {
        this.listItems.push(
            ...list.map((item, index) => {
                return { id: index, name: item.name };
            }),
        );
    }

    /** Событие окончания загрузки страницы */
    public ngOnInit(): void {
        this.initForm();
    }

    public ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    /** Инициализация формы */
    initForm() {
        this.form = this.formBuilder.group({
            certificateRow: [null, [Validators.required]],
            certificate: [null, [Validators.required]],
        });
        this.form
            .get('certificateRow')
            .valueChanges.pipe(
                takeUntil(this._onDestroy),
                map((index) => {
                    this.certificateAlgorithm = null;
                    this.certificateStatus = null;
                    return this.data.certificates[index];
                }),
                filter((value) => !!value),
            )
            .subscribe((value: Certificate) => {
                this.form.get('certificate').setValue(value, { emitEvent: false });
                value.getAlgorithm().then((result) => (this.certificateAlgorithm = result));
                const promise = [];
                promise.push(value._cert.ValidFromDate);
                promise.push(value._cert.ValidToDate);
                promise.push(value._cert.HasPrivateKey());
                promise.push(value.isValid());
                Promise.all(promise)
                    .then((result) => {
                        let validFromDate = result[0] || null;
                        let validToDate = result[1] || null;
                        const hasPrivateKey = result[2] || false;
                        const isValid = result[3] || false;
                        if (validFromDate) {
                            validFromDate = new Date(validFromDate);
                        }
                        if (validToDate) {
                            validToDate = new Date(validToDate);
                        }
                        const now = new Date();
                        if (now < validFromDate) {
                            this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_no_expiration_date;
                        } else if (now > validToDate) {
                            this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_expired;
                        } else if (!hasPrivateKey) {
                            this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_no_private_key_binding;
                        } else if (!isValid) {
                            this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_error_checking_certificate;
                        } else {
                            this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_valid;
                        }
                    })
                    .catch(
                        () =>
                            (this.certificateStatus =
                                this.appService.language_dictionary.system_certificate_status_not_valid),
                    );
            });
    }

    /** Сохранение данных */
    public save() {
        if (this.form.invalid) {
            AppHelper.validateAllFormFields(this.form);
            setTimeout(() => {
                const target = this.formRef.nativeElement.querySelector(
                    '.mat-select.ng-invalid, .mat-input-element.ng-invalid, .mat-checkbox-input.ng-invalid',
                );
                AppHelper.focusInput(target);
            }, 0);
            return;
        }
        this.dialogRef.close({
            save: true,
            data: this.form.get('certificate').value,
        });
    }

    /** Закрыть диалог */
    public close(): void {
        this.dialogRef.close({ save: false });
    }

    /**
     * Выбирает часть строки от каких то символов
     * @param from строка
     * @param what символы начала
     */
    extract(from: string, what: string): string {
        let certName = '';
        const begin = from.indexOf(what);
        if (begin >= 0) {
            const end = from.indexOf(', ', begin);
            certName = end < 0 ? from.substr(begin) : from.substr(begin, end - begin);
        }
        return certName;
    }
}
